import React, { JSX, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip } from '@progress/kendo-react-buttons';
import { useTableKeyboardNavigation } from '@progress/kendo-react-data-tools';
import {
    GRID_COL_INDEX_ATTRIBUTE,
    GridCellProps,
    GridCustomCellProps,
    GridCustomHeaderCellProps,
    GridThAttributes,
} from '@progress/kendo-react-grid';
import { Skeleton } from '@progress/kendo-react-indicators';
import cn from 'clsx';
import { getCodes, Country } from 'country-list';
import { MenuActions } from './menu-action-cell/menu-actions';
import styles from './style.module.scss';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import {
    ChipProps,
    ChipSettings,
    RAGColour,
    RAGColourMap,
} from '@components-clean/core/grid/custom-cell/chip-constants';
import { DataType } from '@components-clean/core/grid/grid-custom-filter/grid-custom-filter.interface';
import {
    GridColumnProps,
    IconCustomCellProps,
    RowMenuAction,
    ICustomToolTipCellProps,
} from '@components-clean/core/grid/grid.interface';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import Tooltip from '@components-clean/core/tooltip';
import { NamedItemFields } from '@queries/generated/fragments.generated';

interface CustomCellProps extends GridCustomCellProps {
    maxLength?: number;
    tooltipText?: string;
    activeField?: string;
    filterType?: GridColumnProps['filter'] | 'float';
    emptyDisplayText?: string;
}

interface CustomThAttributes extends GridThAttributes {
    ariaSort?: 'none' | 'ascending' | 'descending' | undefined;
    ariaColumnIndex?: number;
    ariaSelected?: boolean | undefined;
    ariaDescription?: string;
    ariaLabel?: string;
    style?: React.CSSProperties;
}

interface CustomHeaderCellProps extends GridCustomHeaderCellProps {
    lockedColumn?: boolean;
    multiColumnHeader?: boolean;
    thProps?: CustomThAttributes | null;
    showHeaderToolTip?: boolean;
}

interface CustomCheckBoxHeaderCellProps extends GridCustomHeaderCellProps {
    selectedRowCount: number;
    totalSelectableRows: number;
}

interface MenuActionCellProps extends GridCustomCellProps {
    actions: RowMenuAction[];
    excludeHiddenRowMenuActions?: boolean;
}

const getDataType = (filterType: GridColumnProps['filter'] | 'float') => {
    switch (filterType) {
        case 'text':
            return DataType.String;
        case 'date':
            return DataType.Date;
        case 'numeric':
            return DataType.Int;
        case 'float':
            return DataType.Decimal;
        case 'boolean':
            return DataType.Bool;
        default:
            return DataType.String;
    }
};
interface ITooltipCellProps {
    maxLines?: number;
    isCsv?: boolean;
    activeField?: string;
    inactiveIconTooltip?: string;
    lovArrayField?: string;
    countryArrayField?: string;
    maximumCharacterLength?: number;
}

interface IActiveInactiveCell {
    inactive: (dataItem: INamedItem) => boolean;
    renderValue: (dataItem: INamedItem) => string | JSX.Element;
    multiple?: boolean;
    customField?: string;
}

export interface INamedItem {
    id: string;
    name: string;
    active: boolean;
}

const getActiveFieldValueFromDataItem = (dataItem: unknown, activeField?: string) => {
    if (activeField && dataItem && typeof dataItem === 'object' && Object.hasOwn(dataItem, activeField)) {
        const value = (dataItem as Record<string, unknown>)[activeField];
        if (typeof value === 'string') {
            return value?.toLowerCase() === 'yes';
        }
        if (value === null) {
            return true;
        }
        return Boolean(value);
    }
    return true;
};

const LoadingCell = (props: GridCustomCellProps) => {
    const { tdProps } = props;

    return (
        <td {...tdProps}>
            <Skeleton
                shape="text"
                style={{
                    width: '100%',
                }}
            />
        </td>
    );
};

const RAGValueToColour = (ragValue: string) => {
    if (Number.isNaN(ragValue)) return undefined;

    const num = Number.parseInt(ragValue, 10) as RAGColour;

    return RAGColourMap.get(num);
};

export type CustomCells = {
    BasicCell: (props: CustomCellProps) => React.JSX.Element;
    StandardCell: React.NamedExoticComponent<CustomCellProps>;
    CustomCell: (props: CustomCellProps) => React.JSX.Element;
    HeaderCell: (props: CustomHeaderCellProps) => React.JSX.Element;
    CheckBoxHeaderCell: (props: CustomCheckBoxHeaderCellProps) => React.JSX.Element;
    MenuActionCell: (props: MenuActionCellProps) => React.JSX.Element;
    TooltipCell: (props: Partial<ITooltipCellProps>) => (props: GridCellProps) => React.JSX.Element;
    ActiveInactiveCell: (
        props: Partial<IActiveInactiveCell>,
    ) => (props: GridCellProps) => React.JSX.Element | undefined;
    ChipCell: (props: ChipProps[], settings: ChipSettings) => (props: GridCustomCellProps) => React.JSX.Element;
    IconAndToolTipCell: (props: IconCustomCellProps) => React.JSX.Element;
    RAGChipFieldCell: (valueField: string, ragField: string) => (props: GridCustomCellProps) => React.JSX.Element;
};

/**
 * Updated tooltip cell where the tooltip and content are defined
 * @param props - The tooltip cell props passed in, extends `GridCustomCellProps`
 */
export const CustomToolTipCell = (props: ICustomToolTipCellProps) => {
    const { maxLines, dataItem, children, ...rest } = props;
    const { tooltipItems, content } = dataItem;

    const [showTooltip, setShowTooltip] = React.useState(false);
    const handler = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const tableCellRef = React.useRef<HTMLTableCellElement | null>(null);
    const navigationAttributes = useTableKeyboardNavigation(rest.id);

    let tooltipContent: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> | undefined;
    if (Array.isArray(tooltipItems) && tooltipItems.length > 0) {
        tooltipContent = <div style={{ whiteSpace: 'pre-line' }}>{tooltipItems.join('\n')}</div>;
    } else if (typeof tooltipItems === 'string') {
        tooltipContent = <div style={{ whiteSpace: 'pre-line' }}>tooltipItems</div>;
    } else {
        tooltipContent = undefined;
    }

    if (!tooltipContent) {
        return (
            <td
                {...rest.tdProps}
                className={cn(rest.tdProps?.className)}
                colSpan={rest.colSpan}
                role="gridcell"
                aria-colindex={rest.ariaColumnIndex}
                aria-selected={rest.isSelected}
                {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                {...navigationAttributes}
            >
                {children}
            </td>
        );
    }

    return (
        <td
            {...rest.tdProps}
            ref={tableCellRef}
            onMouseOver={() => {
                if (handler.current) clearTimeout(handler.current);
                setShowTooltip(true);
            }}
            onFocus={() => {}}
            onMouseLeave={() => {
                if (tooltipRef.current && tooltipRef.current.scrollHeight <= tooltipRef.current.clientHeight) {
                    setShowTooltip(false);
                } else
                    handler.current = setTimeout(() => {
                        setShowTooltip(false);
                    }, 50);
            }}
            className={cn(rest.tdProps?.className)}
            colSpan={rest.colSpan}
            role="gridcell"
            aria-colindex={rest.ariaColumnIndex}
            aria-selected={rest.isSelected}
            {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
            {...navigationAttributes}
        >
            <Tooltip anchorElement="target" position="auto" content={() => tooltipContent} open={showTooltip}>
                <div
                    title="#"
                    style={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        lineClamp: maxLines,
                        WebkitLineClamp: maxLines,
                    }}
                >
                    {content}
                </div>
            </Tooltip>
        </td>
    );
};

const useGridCustomCell = (): CustomCells => {
    const BasicCell = ({ tdProps, children, filterType, ...rest }: CustomCellProps) => {
        const isNumericCell =
            filterType && (getDataType(filterType) === DataType.Decimal || getDataType(filterType) === DataType.Int);

        const navigationAttributes = useTableKeyboardNavigation(rest.id);

        return (
            <td
                {...tdProps}
                className={cn(tdProps?.className, { [styles.numericCell]: isNumericCell })}
                colSpan={rest.colSpan}
                role="gridcell"
                aria-colindex={rest.ariaColumnIndex}
                aria-selected={rest.isSelected}
                {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                {...navigationAttributes}
            >
                {children}
            </td>
        );
    };

    const ChipCell = (chips: ChipProps[], settings: ChipSettings) =>
        function inner(props: GridCustomCellProps) {
            const { tdProps, dataItem, field, ...rest } = props;
            const fieldValue = field ? (field.split('.').reduce((obj, key) => obj?.[key], dataItem) as string) : '';

            const chipProps = chips.find((chip) => chip.value?.toLowerCase() === fieldValue?.toLowerCase());

            if (!chipProps) return BasicCell(props);

            return (
                <td {...tdProps} colSpan={rest.colSpan}>
                    {chipProps && (
                        <Chip
                            style={{
                                backgroundColor: chipProps?.backgroundColor,
                                fontSize: 12,
                                cursor: 'default',
                                width: settings.width,
                                border: chipProps.borderColor ? `1px solid ${chipProps.borderColor}` : 'none',
                            }}
                            rounded="large"
                            value={chipProps?.value}
                        >
                            <span className="k-chip-label">{chipProps?.text.toUpperCase()}</span>
                            {chipProps.Icon && <FontAwesomeIcon icon={chipProps?.Icon} />}
                        </Chip>
                    )}
                </td>
            );
        };

    const RAGChipFieldCell = (valueField: string, ragField: string) =>
        function inner(props: GridCustomCellProps) {
            const { tdProps, dataItem, field, ...rest } = props;
            const fieldValue = valueField.split('.').reduce((obj, key) => obj?.[key], dataItem) as string;
            const ragValue = ragField.split('.').reduce((obj, key) => obj?.[key], dataItem) as string;

            if (fieldValue == null || ragValue == null) return BasicCell(props);
            const ragColours = RAGValueToColour(ragValue);
            if (ragColours == null) return BasicCell(props);

            return (
                <td {...tdProps} colSpan={rest.colSpan}>
                    <div className="k-d-flex k-justify-content-end">
                        <Chip
                            style={{
                                backgroundColor: ragColours.color,
                                fontSize: 12,
                                cursor: 'default',
                                border: `1px solid ${ragColours.borderColor}`,
                            }}
                            rounded="large"
                            value={fieldValue}
                        >
                            <span className="k-chip-label">{fieldValue}</span>
                        </Chip>
                    </div>
                </td>
            );
        };

    const StandardCell = React.memo<CustomCellProps>((props) => {
        const {
            tdProps,
            children,
            maxLength,
            activeField,
            dataItem,
            filterType,
            emptyDisplayText = '',
            ...rest
        } = props;
        if (dataItem?.isLoadingCell) return <LoadingCell {...props} />;

        const isNumericCell = filterType === 'numeric' || filterType === 'float';

        const isActive = useMemo(() => {
            return getActiveFieldValueFromDataItem(dataItem, activeField);
        }, [activeField, dataItem]);

        const displayContent = useMemo(() => {
            if (!children) return emptyDisplayText;
            const content = String(children);
            return maxLength && content.length > maxLength ? `${content.slice(0, maxLength)}...` : content;
        }, [children, maxLength, emptyDisplayText]);

        const additionalClasses = cn(styles.standardCell, { [styles.numericCell]: isNumericCell });

        return (
            <td {...tdProps} className={cn(tdProps?.className, additionalClasses)} colSpan={rest.colSpan}>
                <div className="k-d-flex k-justify-content-between k-align-items-center">
                    <div className="ellipsis-overflow ">{displayContent}</div>
                    {!isActive && <InactiveIcon dataTestId="in-active-icon" tooltipPosition="top" />}
                </div>
            </td>
        );
    });

    const CustomCell = (props: CustomCellProps) => {
        const { tdProps, children, tooltipText, filterType, dataItem, ...rest } = props;
        if (dataItem?.isLoadingCell) return <LoadingCell {...props} />;
        const isNumericCell =
            filterType && (getDataType(filterType) === DataType.Decimal || getDataType(filterType) === DataType.Int);

        return (
            <td
                {...tdProps}
                className={cn(tdProps?.className, { [styles.numericCell]: isNumericCell })}
                style={rest.style}
                colSpan={rest.colSpan}
            >
                <Tooltip position="top">
                    <span title={tooltipText ?? ''}>{children}</span>
                </Tooltip>
            </td>
        );
    };

    const HeaderCell = ({
        thProps,
        children,
        multiColumnHeader,
        title,
        lockedColumn,
        showHeaderToolTip,
        ...rest
    }: CustomHeaderCellProps) => {
        const { key, ariaSort, ariaColumnIndex, ariaSelected, ariaDescription, columnId, ...props } = thProps || {};

        return (
            <th
                {...props}
                key={key}
                aria-sort={ariaSort}
                aria-colindex={ariaColumnIndex}
                aria-selected={ariaSelected}
                aria-description={ariaDescription}
                data-multi-column={!!multiColumnHeader}
                data-testid={`column-heading-${rest.id || rest.field}`}
                // columnid is not a valid th attribute, but it is required by Kendo grid. We use the spread
                // operator to prevent an ESLint error as configuration comments do not work here.
                {...{ columnid: columnId }}
            >
                {showHeaderToolTip && (
                    <Tooltip tooltipClassName={styles.tooltipWidth} parentTitle position="bottom">
                        <div className="k-d-flex" title={title}>
                            <div className="k-flex-auto column-title-wrap">{children}</div>
                        </div>
                    </Tooltip>
                )}
                {!showHeaderToolTip && (
                    <div className="k-d-flex">
                        <div className="k-flex-auto column-title-wrap">{children}</div>
                    </div>
                )}
            </th>
        );
    };

    const CheckBoxHeaderCell = ({
        thProps,
        selectionChange,
        selectedRowCount,
        totalSelectableRows,
        children,
        ...rest
    }: CustomCheckBoxHeaderCellProps) => {
        const { key, ariaSort, ariaColumnIndex, ariaSelected, ariaDescription, columnId, ...props } = thProps || {};
        return (
            <th
                {...props}
                key={key}
                aria-sort={ariaSort}
                aria-colindex={ariaColumnIndex}
                aria-selected={ariaSelected}
                aria-description={ariaDescription}
                data-multi-column={false}
                data-testid={`column-heading-${rest.id || rest.field}`}
                // columnid is not a valid th attribute, but it is required by Kendo grid. We use the spread
                // operator to prevent an ESLint error as configuration comments do not work here.
                {...{ columnid: columnId }}
            >
                <CoreCheckbox
                    className="!k-d-block k-text-center"
                    value={
                        selectedRowCount > 0 && selectedRowCount < totalSelectableRows
                            ? null
                            : selectedRowCount > 0 && selectedRowCount === totalSelectableRows
                    }
                    data-testid={`checkbox-column-selectall-${rest.id || rest.field}`}
                    onClick={(event) => selectionChange({ syntheticEvent: event })}
                    style={{ boxSizing: 'border-box' }}
                />
            </th>
        );
    };

    const MenuActionCell = (props: MenuActionCellProps) => {
        const { tdProps, actions, dataItem, excludeHiddenRowMenuActions } = props;
        if (dataItem?.isLoadingCell) return <LoadingCell {...props} />;
        // Do not apply the click handler to the td, to stop the click propagating
        // which could cause the onSelect event to fire, which will trigger a re-render
        // meaning that the menu action doesn't actually get clicked
        return (
            <td {...tdProps} onClick={undefined} className={cn(tdProps?.className, '!k-px-1')}>
                <MenuActions
                    actions={actions}
                    dataItem={dataItem as unknown}
                    excludeHiddenRowMenuActions={excludeHiddenRowMenuActions}
                />
            </td>
        );
    };

    const TooltipCell = ({
        maxLines = 3,
        isCsv = false,
        activeField,
        inactiveIconTooltip,
        lovArrayField,
        countryArrayField,
        maximumCharacterLength,
    }: Partial<ITooltipCellProps> = {}) =>
        function inner(props: GridCustomCellProps) {
            const { t } = useTranslation();
            const { className, dataItem, style, field, ...rest } = props;
            if (dataItem?.isLoadingCell) return <LoadingCell {...props} />;
            const navigationAttributes = useTableKeyboardNavigation(rest.id);
            const countryCodes = useMemo(() => getCodes(), []);

            const isActive = useMemo(() => {
                if (lovArrayField) {
                    const lovArray = dataItem?.[lovArrayField] as NamedItemFields[];
                    return lovArray.every((lov) => lov && lov.active);
                }
                if (countryArrayField) {
                    const countryArray = dataItem?.[countryArrayField] as Country[];
                    return countryArray.every((country) => countryCodes.includes(country.code));
                }
                return getActiveFieldValueFromDataItem(dataItem, activeField);
            }, [activeField, dataItem]);

            let fieldValue = field ? (field.split('.').reduce((obj, key) => obj?.[key], dataItem) as string) : '';

            if (fieldValue && maximumCharacterLength) {
                fieldValue = fieldValue.substring(0, maximumCharacterLength);
            }

            const [showTooltip, setShowTooltip] = React.useState(false);
            const handler = useRef<ReturnType<typeof setTimeout> | null>(null);
            const tooltipRef = useRef<HTMLDivElement>(null);
            const tableCellRef = React.useRef<HTMLTableCellElement | null>(null);

            const cellStringContent = (
                <div
                    title="#"
                    style={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        lineClamp: maxLines,
                        WebkitLineClamp: maxLines,
                    }}
                >
                    {fieldValue}
                </div>
            );

            return (
                <td
                    {...rest.tdProps}
                    ref={tableCellRef}
                    onMouseOver={() => {
                        if (handler.current) clearTimeout(handler.current);
                        setShowTooltip(true);
                    }}
                    onFocus={() => {}}
                    onMouseLeave={() => {
                        if (tooltipRef.current && tooltipRef.current.scrollHeight <= tooltipRef.current.clientHeight) {
                            setShowTooltip(false);
                        } else
                            handler.current = setTimeout(() => {
                                setShowTooltip(false);
                            }, 50);
                    }}
                    className={cn(className || '', rest.tdProps?.className)}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    <Tooltip
                        anchorElement="target"
                        position="auto"
                        open={showTooltip}
                        content={() => (
                            <div
                                ref={tooltipRef}
                                style={{
                                    whiteSpace: isCsv ? 'pre-line' : 'initial',
                                    maxWidth: 300,
                                    maxHeight: 400,
                                    overflowY: 'auto',
                                }}
                            >
                                {isCsv ? fieldValue.replaceAll(', ', '\n') : fieldValue}
                            </div>
                        )}
                    >
                        {isActive ? (
                            cellStringContent
                        ) : (
                            <div className="k-d-flex k-justify-content-between k-align-items-center">
                                {cellStringContent}
                                {!isActive && (
                                    <InactiveIcon
                                        toolTipText={
                                            lovArrayField || countryArrayField
                                                ? t('common:multipleRecordsInactiveIcon')
                                                : inactiveIconTooltip
                                        }
                                        dataTestId="in-active-icon"
                                        tooltipPosition="top"
                                    />
                                )}
                            </div>
                        )}
                    </Tooltip>
                </td>
            );
        };

    const ActiveInactiveCell = ({ inactive, renderValue, multiple, customField }: Partial<IActiveInactiveCell> = {}) =>
        function inner(props: GridCustomCellProps) {
            const { className, dataItem, style, field, ...rest } = props;
            const navigationAttributes = useTableKeyboardNavigation(rest.id);
            const tooltipRef = useRef<HTMLDivElement>(null);
            const tableCellRef = React.useRef<HTMLTableCellElement | null>(null);
            const [showTooltip, setShowTooltip] = React.useState(false);
            const handler = useRef<ReturnType<typeof setTimeout> | null>(null);
            const itemField = customField || field;
            if (!itemField) return;
            const fieldValue = itemField.split('.').reduce((obj, key) => obj?.[key], dataItem) as INamedItem;

            const getContentItem = (item: INamedItem) => (
                <div className="k-d-flex k-justify-content-between k-align-items-center">
                    <div
                        title="#"
                        style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            lineClamp: 3,
                            WebkitLineClamp: 3,
                            flexGrow: 1,
                        }}
                    >
                        {renderValue && renderValue(item)}
                    </div>
                    {inactive && inactive(item) && <InactiveIcon dataTestId="in-active-icon" tooltipPosition="top" />}
                </div>
            );

            const content = multiple
                ? dataItem[itemField!]?.map((value: never, index: number, array: []) => {
                      return (
                          <span style={{ display: 'inline-block' }}>
                              {getContentItem(value)}
                              {index < array.length - 1 && <span style={{ marginRight: '5px' }}>,</span>}
                          </span>
                      );
                  })
                : getContentItem(fieldValue);

            return (
                <td
                    {...rest.tdProps}
                    ref={tableCellRef}
                    className={cn(className || '', rest.tdProps?.className)}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                    onMouseOver={() => {
                        if (handler.current) clearTimeout(handler.current);
                        setShowTooltip(true);
                    }}
                    onFocus={() => {}}
                    onMouseLeave={() => {
                        if (tooltipRef.current && tooltipRef.current.scrollHeight <= tooltipRef.current.clientHeight) {
                            setShowTooltip(false);
                        } else
                            handler.current = setTimeout(() => {
                                setShowTooltip(false);
                            }, 50);
                    }}
                >
                    <Tooltip
                        anchorElement="target"
                        position="auto"
                        open={showTooltip}
                        content={() => (
                            <div
                                ref={tooltipRef}
                                style={{
                                    whiteSpace: 'initial',
                                    maxWidth: 300,
                                    maxHeight: 400,
                                    overflowY: 'auto',
                                }}
                            >
                                {renderValue && renderValue(fieldValue)}
                            </div>
                        )}
                    >
                        {content}
                    </Tooltip>
                </td>
            );
        };

    const IconAndToolTipCell = ({ dataItem, children, ...rest }: IconCustomCellProps) => {
        const { icon, tooltipItems, tooltipItemsCount, title, noItemsMessage } = dataItem;
        const titleText = tooltipItems?.length ? `${title}: \n ${tooltipItems.join(' \n ')}` : noItemsMessage;

        const isInactive = (Number(dataItem.tooltipItemsCount) || 0) === 0;

        const content = (
            <div className="k-d-flex" title={titleText}>
                <FontAwesomeIcon
                    icon={['far', icon]}
                    style={{
                        pointerEvents: 'none',
                        color: isInactive ? '#9EA8B6' : 'inherit',
                    }}
                />
            </div>
        );

        return (
            <BasicCell dataItem={dataItem} {...rest}>
                <div
                    className="k-d-flex k-align-items-center k-justify-content-center"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.25rem',
                        width: '100%',
                    }}
                >
                    <Tooltip
                        content={() =>
                            tooltipItems?.length ? (
                                <div style={{ whiteSpace: 'pre-line' }}>
                                    {title}:{'\n'}
                                    {tooltipItems.join('\n')}
                                </div>
                            ) : (
                                <div style={{ whiteSpace: 'pre-line' }}>{noItemsMessage}</div>
                            )
                        }
                        position="bottom"
                    >
                        <div className="k-flex-auto column-title-wrap">{content}</div>
                    </Tooltip>
                    <span> {tooltipItemsCount ?? 0} </span>
                </div>
            </BasicCell>
        );
    };

    return {
        BasicCell,
        StandardCell,
        CustomCell,
        HeaderCell,
        CheckBoxHeaderCell,
        MenuActionCell,
        TooltipCell,
        ActiveInactiveCell,
        ChipCell,
        IconAndToolTipCell,
        RAGChipFieldCell,
    };
};

export default useGridCustomCell;
